import {
  OperationalInfrastructureInfo,
  StationInformation,
  Train,
} from "@vygruppen/vy-train-map";
import { GlobalEventResponse } from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import { DayPlanTrackingType } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/types";
import { TrainInformationAggregated } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import { TrainResumedResponse } from "features/Header/TrainResumed/useTrainResumed";
import { DropsLogEntry } from "features/TodoOploggSidebar/Oplogg/OploggElement/types";
import { Todo } from "features/TodoOploggSidebar/Todo/todoList/todoElement/TodoElement";

export enum BackendIdentifier {
  TRAINMAP_BACKEND = "TRAINMAP_BACKEND",
  DROPS_BACKEND = "DROPS_BACKEND",
}

// =============================================
// ========== WEBSOCKET MESSAGE TOPICS =========
// =============================================
export enum WebsocketTopic {
  // Both
  STATION_EVENT = "STATION_EVENT", // Includes sensitive messages from Drops backend, but not from Trainmap backend

  // Drops Backend
  TODO = "TODO",
  DROPS_LOG = "DROPS_LOG_V2",
  DAY_PLAN_TRACKING = "DAY_PLAN_TRACKING",
  TRAIN_INFORMATION = "TRAIN_INFORMATION",
  TRAIN_RESUMED = "TRAIN_RESUMED",
  GLOBAL_INFORMATION = "GLOBAL_INFORMATION",
  // AFFECTED_TRAIN = "AFFECTED_TRAIN", // Exists, but no messages are sent on this topic yet

  // Trainmap backend
  OTR = "OTR",
  TRAIN_UPDATE_NO = "TRAIN_UPDATE_NO",
  TRAIN_UPDATE_SE = "TRAIN_UPDATE_SE",
  INFRASTRUCTURE_EVENT = "INFRASTRUCTURE_EVENT",
}

type ValidDropsTopics =
  | WebsocketTopic.STATION_EVENT
  | WebsocketTopic.TODO
  | WebsocketTopic.DROPS_LOG
  | WebsocketTopic.DAY_PLAN_TRACKING
  | WebsocketTopic.TRAIN_INFORMATION
  | WebsocketTopic.TRAIN_RESUMED
  | WebsocketTopic.GLOBAL_INFORMATION;

type ValidTrainmapTopics =
  | WebsocketTopic.STATION_EVENT
  | WebsocketTopic.OTR
  | WebsocketTopic.TRAIN_UPDATE_NO
  | WebsocketTopic.TRAIN_UPDATE_SE
  | WebsocketTopic.INFRASTRUCTURE_EVENT;

export type ValidTopicsForBackend = {
  [BackendIdentifier.DROPS_BACKEND]: ValidDropsTopics;
  [BackendIdentifier.TRAINMAP_BACKEND]: ValidTrainmapTopics;
};

// =============================================
// ========== WEBSOCKET MESSAGE TYPES ==========
// =============================================
export type WebsocketMessage = TrainMapWebsocketMessage | DropsWebsocketMessage;

//  DROPS BACKEND
export type DropsWebsocketMessage =
  | TodoMessage
  | DropsLogMessage
  | DayPlanTrackingMessage
  | TrainInformationMessage
  | StationInformationMessage
  | GlobalInformationMessage
  | TrainResumedMessage;

export type DropsLogMessage = {
  topic: WebsocketTopic.DROPS_LOG;
  message: DropsLogEntry;
};

export type DayPlanTrackingMessage = {
  topic: WebsocketTopic.DAY_PLAN_TRACKING;
  message: DayPlanTrackingType;
};

export type TrainInformationMessage = {
  topic: WebsocketTopic.TRAIN_INFORMATION;
  message: TrainInformationAggregated;
};

export type StationInformationMessage = {
  topic: WebsocketTopic.STATION_EVENT;
  message: StationInformation;
};

export type GlobalInformationMessage = {
  topic: WebsocketTopic.GLOBAL_INFORMATION;
  message: GlobalEventResponse;
};

export type TrainResumedMessage = {
  topic: WebsocketTopic.TRAIN_RESUMED;
  message: TrainResumedResponse;
};

type TodoMessageType = "ADD" | "UPDATE" | "DELETE";

export type TodoMessageData = {
  action: TodoMessageType;
  todo: Todo;
};

export type TodoMessage = {
  topic: WebsocketTopic.TODO;
  message: TodoMessageData;
};

//  TRAINMAP BACKEND
export type TrainMapWebsocketMessage =
  | TrainUpdateMessage
  | InfrastructureInformationMessage
  | StationInformationMessage;

type TrainmapMessageType = "UPDATE" | "DELETE";

export type TrainUpdateMessageData = {
  type: TrainmapMessageType;
  train: Train;
  timestamp: number;
};

export type TrainUpdateMessage = {
  topic:
    | WebsocketTopic.OTR
    | WebsocketTopic.TRAIN_UPDATE_NO
    | WebsocketTopic.TRAIN_UPDATE_SE;
  message: TrainUpdateMessageData;
};

export type InfrastructureInformationMessageData = {
  type: TrainmapMessageType;
  message: OperationalInfrastructureInfo;
  topic: WebsocketTopic.INFRASTRUCTURE_EVENT;
};

export type InfrastructureInformationMessage = {
  topic: WebsocketTopic.INFRASTRUCTURE_EVENT;
  message: InfrastructureInformationMessageData;
};

// =============================================
// ========= WEBSOCKET MESSAGE HANDLERS ========
// =============================================
type MessageHandlerForTopic<Topic extends WebsocketTopic> = (
  messageData: MessageDataForTopic[Topic],
) => void;

export type MessageHandlersForBackend = {
  [Backend in BackendIdentifier]: {
    [Topic in ValidTopicsForBackend[Backend]]: MessageHandlerForTopic<Topic>;
  };
};

export type MessageDataForTopic = {
  [WebsocketTopic.STATION_EVENT]: StationInformationMessage;
  [WebsocketTopic.TODO]: TodoMessage;
  [WebsocketTopic.DROPS_LOG]: DropsLogMessage;
  [WebsocketTopic.DAY_PLAN_TRACKING]: DayPlanTrackingMessage;
  [WebsocketTopic.TRAIN_INFORMATION]: TrainInformationMessage;
  [WebsocketTopic.TRAIN_RESUMED]: TrainResumedMessage;
  [WebsocketTopic.GLOBAL_INFORMATION]: GlobalInformationMessage;
  [WebsocketTopic.STATION_EVENT]: StationInformationMessage;
  [WebsocketTopic.OTR]: TrainUpdateMessage;
  [WebsocketTopic.TRAIN_UPDATE_NO]: TrainUpdateMessage;
  [WebsocketTopic.TRAIN_UPDATE_SE]: TrainUpdateMessage;
  [WebsocketTopic.INFRASTRUCTURE_EVENT]: InfrastructureInformationMessage;
};

export type WebsocketSendMessage =
  | WebsocketSetTopicMessage
  | WebsocketPingMessage;

type TopicsChange = {
  add: WebsocketTopic[];
  remove: WebsocketTopic[];
};

export enum WebsocketMessageAction {
  SET_TOPICS = "setTopics",
  PING = "ping",
}

export type WebsocketSetTopicMessage = {
  action: WebsocketMessageAction.SET_TOPICS;
  topics: TopicsChange;
};

export type WebsocketPingMessage = { action: WebsocketMessageAction.PING };

export type WebsocketReceiveMessage<Topic extends WebsocketTopic> =
  | MessageDataForTopic[Topic]
  | InfrastructureInformationMessageData
  | TrainUpdateMessageData;

// =============================================
// ======== WORKER PORT MESSAGE HANDLERS =======
// =============================================
export type WorkerPortId = string;

export type SharedWorkerReturnMessageData<Topic extends WebsocketTopic> =
  | { type: "connected"; id: WorkerPortId }
  | { type: "message"; messageContent: MessageDataForTopic[Topic] }
  | { type: "uninitialised"; original: PortMessageEventData }
  | { type: "reauthenticate" }
  | { type: "resubscribe"; id: WorkerPortId };

export type PortMessageEventData =
  | { type: "add"; id: WorkerPortId; topics: Set<WebsocketTopic> }
  | { type: "remove"; id: WorkerPortId; topics: Set<WebsocketTopic> }
  | { type: "close"; id: WorkerPortId }
  | { type: "reactivated"; id: WorkerPortId }
  | { type: "token"; token: string };
